import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { TripRouteScheduleRoute } from '@/shared/api/routes'

export interface UseRouteStartEndMarkersProps {
  routes?: TripRouteScheduleRoute[]
}

/**
 * Hook for displaying start/end point markers for trip routes
 * Shows numbered markers (1 for start, 2 for end) on the map
 */
export function useRouteStartEndMarkers({ routes }: UseRouteStartEndMarkersProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const markersRef = useRef<mapboxgl.Marker[]>([])

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !routes) {
      return
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    // Create markers for each route's start/end points
    routes.forEach((routeSchedule, routeIndex) => {
      const { association } = routeSchedule

      // Start point marker
      if (association.start_latitude != null && association.start_longitude != null) {
        const startEl = document.createElement('div')
        startEl.className = 'route-start-marker'
        startEl.innerHTML = `
          <div class="flex items-center justify-center w-8 h-8 rounded-full bg-adventure-600 text-white text-xs font-bold shadow-lg border-2 border-white">
            1
          </div>
        `
        startEl.style.cursor = 'pointer'

        const startMarker = new mapboxgl.Marker({
          element: startEl,
          anchor: 'center'
        })
          .setLngLat([association.start_longitude, association.start_latitude])
          .addTo(map.current!)

        markersRef.current.push(startMarker)
      }

      // End point marker
      if (association.end_latitude != null && association.end_longitude != null) {
        const endEl = document.createElement('div')
        endEl.className = 'route-end-marker'
        endEl.innerHTML = `
          <div class="flex items-center justify-center w-8 h-8 rounded-full bg-earth-600 text-white text-xs font-bold shadow-lg border-2 border-white">
            2
          </div>
        `
        endEl.style.cursor = 'pointer'

        const endMarker = new mapboxgl.Marker({
          element: endEl,
          anchor: 'center'
        })
          .setLngLat([association.end_longitude, association.end_latitude])
          .addTo(map.current!)

        markersRef.current.push(endMarker)
      }
    })

    return () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
    }
  }, [map, isLoaded, styleLoaded, routes])
}

